/**
 * Order card: native mobile app style.
 * Shows customer, amount, status with expandable details.
 * Tickets open in dedicated page on tap.
 */

import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconChevronRight,
  IconTicket,
  IconAlertTriangle,
  IconMessage,
} from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import {
  getOrderStatusConfig,
  getCurrencySymbol,
  formatCustomerName,
  type AdminOrder,
} from "../../shared/types.ts";
import { formatDateBangkok } from "../../../../lib/bangkok-time.ts";
import { SendOrderMessageModal } from "./SendOrderMessageModal.tsx";

export interface OrderCardProps {
  order: AdminOrder;
  /** Show event title (for live feed / cross-event views) */
  showEvent?: boolean;
  /** Compact mode (less padding, smaller text) */
  compact?: boolean;
  /** Expandable with ticket details */
  expandable?: boolean;
  /** Controlled expanded state (for single-expand behavior) */
  isExpanded?: boolean;
  /** Callback when card is toggled (for controlled mode) */
  onToggle?: (orderId: string) => void;
  /** Callback when resend is clicked */
  onResend?: (orderId: string) => void;
  /** Callback when regenerate is clicked */
  onRegenerate?: (orderId: string) => void;
  /** Callback when generate tickets is clicked */
  onGenerate?: (orderId: string) => void;
  /** Callback when message was sent to user (e.g. to refetch) */
  onMessageSent?: (orderId: string) => void;
  /** Is currently processing an action */
  isProcessing?: boolean;
}

export function OrderCard({
  order,
  showEvent = false,
  compact = false,
  expandable = true,
  isExpanded: controlledExpanded,
  onToggle,
  onGenerate,
  onResend,
  onMessageSent,
  isProcessing = false,
}: OrderCardProps) {
  // Support both controlled and uncontrolled modes
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [sendMessageOrder, setSendMessageOrder] = useState<AdminOrder | null>(null);
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const statusConfig = getOrderStatusConfig(order.status);
  const customerName = formatCustomerName(order.customer);
  const totalQuantity = order.items.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const hasTickets = order.tickets.length > 0;

  const handleToggle = () => {
    if (!expandable) return;
    haptic("light");

    const willExpand = !isExpanded;

    // Use controlled or uncontrolled mode
    if (isControlled && onToggle) {
      onToggle(order.id);
    } else {
      setInternalExpanded(willExpand);
    }

    // Scroll expanded card into view only if bottom goes below viewport
    // Don't scroll if card is already fully visible
    if (willExpand && cardRef.current) {
      setTimeout(() => {
        const card = cardRef.current;
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const tabBarHeight = 100; // TabBar + safe area
        const visibleBottom = viewportHeight - tabBarHeight;

        // Only scroll if card bottom is below visible area
        if (rect.bottom > visibleBottom) {
          card.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 100);
    }
  };

  const handleTicketTap = (ticketId: string) => {
    haptic("light");
    // Pass all ticket IDs and orderId for navigation and actions
    const ticketIds = order.tickets.map((t) => t.id);
    navigate(`/admin/tickets/${ticketId}/view`, {
      state: {
        from: location.pathname,
        ticketIds,
        orderId: order.id,
        scrollY: window.scrollY,
      },
    });
  };

  return (
    <>
      {/* scroll-margin-bottom accounts for TabBar height (~80px + safe area) */}
      <div
        ref={cardRef}
        data-order-id={order.id}
        className={`border-b transition-colors ${
          isExpanded
            ? "bg-slate-100/80 border-slate-200 shadow-sm"
            : "border-slate-100"
        }`}
        style={{ scrollMarginBottom: "100px" }}
      >
        {/* Main row */}
        <button
          type="button"
          onClick={handleToggle}
          disabled={!expandable}
          className={`w-full flex items-center gap-3 text-left active:bg-slate-50 transition-colors ${
            compact ? "px-3 py-2.5" : "px-4 py-3"
          } ${!expandable ? "cursor-default" : ""}`}
        >
          <div className="flex-1 min-w-0">
            {/* Customer + ID */}
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-slate-900 truncate text-[14px]">
                {customerName}
              </span>
              <span className="text-slate-400 font-mono text-[10px] shrink-0">
                #{order.id.slice(-6).toUpperCase()}
              </span>
            </div>

            {/* Event title */}
            {showEvent && (
              <div className="text-slate-600 truncate text-[12px] mb-0.5">
                {order.event.title}
              </div>
            )}

            {/* Date + quantity */}
            <div className="text-slate-400 text-[11px]">
              {formatDateBangkok(order.createdAt)}
              <span className="mx-1">·</span>
              {totalQuantity} билет
              {totalQuantity === 1 ? "" : totalQuantity < 5 ? "а" : "ов"}
            </div>
          </div>

          {/* Amount + Status */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="font-semibold text-slate-900 text-[14px]">
              {order.totalAmount} {getCurrencySymbol(order.currency)}
            </span>
            <span
              className={`inline-block px-2 py-0.5 font-medium rounded-md text-[10px] ${statusConfig.color}`}
            >
              {statusConfig.label}
            </span>
          </div>

          {expandable && (
            <IconChevronRight
              className={`w-4 h-4 text-slate-300 shrink-0 transition-transform ${
                isExpanded ? "rotate-90" : ""
              }`}
            />
          )}
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-3 pt-0 bg-slate-50/90 border-t border-slate-200">
            {/* Order items */}
            <div className="py-3">
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
                Состав заказа
              </div>
              <p className="text-[13px] text-slate-800">
                {order.items.map((item, idx) => (
                  <span key={idx}>
                    {item.ticketType.name} × {item.quantity}
                    {idx < order.items.length - 1 && ", "}
                  </span>
                ))}
              </p>
            </div>

            {/* Tickets - tap to view fullscreen */}
            {hasTickets ? (
              <div className="pb-3">
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Билеты
                </div>
                <div className="flex flex-wrap gap-2">
                  {order.tickets.map((ticket) => {
                    const isUsed = ticket.status === "USED";
                    return (
                      <button
                        key={ticket.id}
                        type="button"
                        onClick={() => handleTicketTap(ticket.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-colors active:scale-95 ${
                          isUsed
                            ? "bg-slate-100 border-slate-200 text-slate-400"
                            : "bg-teal-50 border-teal-200 text-teal-700"
                        }`}
                      >
                        <IconTicket className="w-4 h-4" />
                        <span className="text-[13px] font-medium">
                          №{ticket.ticketNumber}
                        </span>
                        {isUsed && <span className="text-[10px] ml-1">✓</span>}
                      </button>
                    );
                  })}
                </div>
                {order.status === "TICKETS_PENDING_SEND" && onResend && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic("light");
                      onResend(order.id);
                    }}
                    disabled={isProcessing}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-teal-500 text-white font-medium text-[13px] disabled:opacity-50 active:bg-teal-600"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 2L11 13" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                    </svg>
                    Отправить пользователю
                  </button>
                )}
              </div>
            ) : (
              /* Status info when no tickets */
              <div className="pb-3">
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Статус
                </div>
                {(order.status === "CREATED" ||
                  order.status === "AWAITING_PAYMENT") && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200">
                      <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                        <span className="text-lg">⏳</span>
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">
                          Ожидает оплаты
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5">
                          Пользователь ещё не загрузил чек об оплате
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {order.status === "PENDING_VERIFICATION" && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <span className="text-lg">🔍</span>
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-amber-700">
                        Ожидает проверки
                      </p>
                      <p className="text-[12px] text-amber-600 mt-0.5">
                        Чек загружен, требуется подтверждение оплаты
                      </p>
                    </div>
                  </div>
                )}
                {order.status === "VERIFIED" && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                        <IconTicket className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-green-700">
                          Оплата подтверждена
                        </p>
                        <p className="text-[12px] text-green-600 mt-0.5">
                          Билеты не сгенерированы
                        </p>
                      </div>
                    </div>
                    {onGenerate && (
                      <button
                        type="button"
                        onClick={() => {
                          haptic("light");
                          onGenerate(order.id);
                        }}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-teal-500 text-white font-medium text-[13px] disabled:opacity-50 active:bg-teal-600"
                      >
                        <IconTicket className="w-4 h-4" />
                        Сгенерировать билет
                      </button>
                    )}
                  </div>
                )}
                {order.status === "TICKET_ISSUED" && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                        <IconAlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-amber-700">
                          Билеты отсутствуют
                        </p>
                        <p className="text-[12px] text-amber-600 mt-0.5">
                          Заказ оплачен, но билеты не найдены в базе
                        </p>
                      </div>
                    </div>
                    {onGenerate && (
                      <button
                        type="button"
                        onClick={() => {
                          haptic("light");
                          onGenerate(order.id);
                        }}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-teal-500 text-white font-medium text-[13px] disabled:opacity-50 active:bg-teal-600"
                      >
                        <IconTicket className="w-4 h-4" />
                        Сгенерировать билет
                      </button>
                    )}
                  </div>
                )}
                {(order.status === "CANCELLED" ||
                  order.status === "EXPIRED") && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200">
                      <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                        <span className="text-lg">❌</span>
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-600">
                          {order.status === "EXPIRED"
                            ? "Срок оплаты истёк"
                            : "Заказ отменён"}
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5">
                          {order.status === "EXPIRED"
                            ? "Заказ не был оплачен в установленный срок"
                            : "Заказ был отменён пользователем или администратором"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {order.status === "REJECTED" && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <span className="text-lg">🚫</span>
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-red-700">
                        Оплата отклонена
                      </p>
                      <p className="text-[12px] text-red-600 mt-0.5">
                        Чек не прошёл проверку
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Написать пользователю — одна кнопка внизу */}
            <div className="pt-3 mt-3 border-t border-slate-200/80">
              <button
                type="button"
                onClick={() => {
                  haptic("light");
                  setSendMessageOrder(order);
                }}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-amber-500 text-white font-medium text-[13px] disabled:opacity-50 active:bg-amber-600"
              >
                <IconMessage className="w-4 h-4" />
                Написать пользователю
              </button>
            </div>
          </div>
        )}
      </div>
      {sendMessageOrder && (
        <SendOrderMessageModal
          order={sendMessageOrder}
          onClose={() => setSendMessageOrder(null)}
          onSent={() => {
            setSendMessageOrder(null);
            onMessageSent?.(sendMessageOrder.id);
          }}
        />
      )}
    </>
  );
}
